import React from 'react';
import { useSelector } from 'react-redux';
import useTranslation from '@src/hooks/useTranslation';
import styled from 'styled-components';
import { withFormik } from 'formik';
import { isEmpty } from 'lodash';

import { IColors } from '@src/types/theme';
import * as selectors from '@src/store/selectors';
import { BUTTON_VARIANT_OUTLINED } from '@src/const/common';

import { SmileIcon } from '@src/components/common/icon';
import Rating from '@src/components/common/rating';
import Button from '@src/components/common/button';
import Modal from '@src/components/common/modal';

const MODAL_WIDTH = {
	desktop: 768,
	tablet: 688,
};

const ratingStyle = {
	fontSize: 36,
};

const ContentTop = styled.div`
	padding-bottom: 32px;
	border-bottom: 1px solid #F0F0F0;

	@media ${props => props.theme.device.mobile} {
		padding-bottom: 24px;
	}
`;

const WelcomeReview = styled.div`
	display: flex;
	align-items: center;
`;

const WelcomeReviewText = styled.div`
	margin-left: 12px;
	font-size: 20px;
	color: ${props => props.theme.colors.primary};

	@media ${props => props.theme.device.mobile} {
		font-size: 18px;
	}
`;

const ReviewDescription = styled.div`
	margin-top: 12px;

	@media ${props => props.theme.device.lgDesktop} {
		margin-top: 16px;
	}

	@media ${props => props.theme.device.desktop} {
		margin-top: 16px;
	}
`;

const ContentBottom = styled.div`
	margin-top: 32px;

	@media ${props => props.theme.device.mobile} {
		margin-top: 24px;
	}
`;

const Block = styled.div`
	&:not(:first-child) {
		margin-top: 32px;
	}

	@media ${props => props.theme.device.mobile} {
		&:not(:first-child) {
			margin-top: 24px;
		}
	}
`;

const BlockTitle = styled.div`
	margin-bottom: 12px;
	font-weight: 700;
	color: #101010;
`;

const Required = styled.span`
	margin-left: 4px;
	color: #d0021b;
`;

const ReviewInput = styled.textarea`
	width: 100%;
	padding: 12px 16px;
	border: 1px solid #DDDDDD;
	border-radius: 8px;
	outline-color: ${props => (props.error ? '#d0021b' : 'invert')};

	&:focus {
		outline-color: ${props => (props.error ? '#d0021b' : props.theme.colors.primary)};
	}
`;

const CancelButton = styled(Button)`
	width: 140px;
	padding: 8px 24px;

	@media ${props => props.theme.device.mobile} {
		width: 166px;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
	}
`;

const SubmitButton = styled(Button)`
	width: 140px;
	padding: 8px 24px;
	margin-left: 16px;

	@media ${props => props.theme.device.mobile} {
		width: 166px;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-left: 12px;
	}
`;

const ErrorMsg = styled.div`
	margin-top: 8px;
	font-size: 14px;
	color: #d0021b;
`;

interface IFormValues {
	rating: number;
	message: string;
}

interface IFormErrors {
	rating?: boolean;
	message?: boolean;
}

interface IAddReviewModal {
	values: IFormValues;
	errors: IFormErrors;
	dirty: boolean;
	isValid: boolean;
	isSubmitting: boolean;
	isActive: boolean;
	setFieldValue: (string, number) => void;
	handleChange: () => void;
	handleSubmit: () => void;
	handleClose: () => void;
}

const genModalFooter = ({ cancelTex, submitTex, disabledSubmit, handleSubmit, handleClose }: {
	cancelTex: string;
	submitTex: string;
	disabledSubmit: boolean;
	handleSubmit: () => void;
	handleClose: () => void;
}): JSX.Element => (
	<>
		<CancelButton variant={BUTTON_VARIANT_OUTLINED} handleClick={handleClose}>{cancelTex}</CancelButton>
		<SubmitButton isDisable={disabledSubmit} handleClick={handleSubmit}>{submitTex}</SubmitButton>
	</>
);

const AddReviewModal = ({
	values, errors, dirty, isValid, isSubmitting, isActive, setFieldValue, handleChange, handleSubmit, handleClose,
}: IAddReviewModal): JSX.Element => {
	const disabledSubmit = !dirty || !isValid || isSubmitting;
	const { t } = useTranslation();
	const { colors } = useSelector(selectors.getColorTheme) as { colors: IColors };

	const handleChangeRating = value => { setFieldValue('rating', value); };
	const modalFooter = genModalFooter({
		cancelTex: t('modal:cancel'),
		submitTex: t('modal:submit'),
		disabledSubmit,
		handleSubmit,
		handleClose,
	});

	return (
		<Modal
			modalWidth={MODAL_WIDTH}
			isActive={isActive}
			headerText={t('course:write_course_review')}
			footer={modalFooter}
			handleClose={handleClose}
		>
			<ContentTop>
				<WelcomeReview>
					<SmileIcon iconColor={colors.primary} />
					<WelcomeReviewText>{t('course:welcome_to_review')}</WelcomeReviewText>
				</WelcomeReview>
				<ReviewDescription>{t('course:review_description')}</ReviewDescription>
			</ContentTop>
			<ContentBottom>
				<Block>
					<BlockTitle>
						{t('course:write_rating')}
						<Required>*</Required>
					</BlockTitle>
					<Rating
						disabled={false}
						value={values.rating}
						style={ratingStyle}
						handleChange={handleChangeRating}
					/>
					{errors.rating && <ErrorMsg>{t('error_msg:rating_required')}</ErrorMsg>}
				</Block>
				<Block>
					<BlockTitle>
						{t('course:course_review')}
						<Required>*</Required>
					</BlockTitle>
					<ReviewInput
						error={errors.message}
						name="message"
						value={values.message}
						rows="4"
						placeholder={t('course:enter_review')}
						onChange={handleChange}
					/>
					{errors.message && <ErrorMsg>{t('error_msg:review_required')}</ErrorMsg>}
				</Block>
			</ContentBottom>
		</Modal>
	);
};

// TODO: Should prevent to use `any`
export default withFormik({
	mapPropsToValues: ({ review }: any) => ({
		rating: isEmpty(review) ? 0 : review.rating,
		message: isEmpty(review) ? '' : review.message,
	}),
	validate: ({ rating, message }) => {
		const errors = {};

		/* eslint-disable dot-notation */
		if (rating <= 0) {
			errors['rating'] = true;
		}

		if (!message.trim()) {
			errors['message'] = true;
		}
		/* eslint-enable dot-notation */

		return errors;
	},
	handleSubmit: ({ rating, message }, { props }) => {
		const { modalType, handleSubmitReview, handleClose } = props;

		handleSubmitReview({ modalType, message, rating });
		handleClose();
	},
})(AddReviewModal);
